import { inject, Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';

/**
 * Client for the assistant callables (see ../blazebite-functions/NETWORK_CONTRACT.md,
 * "Chat Functions" and "Chat Admin Functions").
 *
 * Two surfaces share one decision shape so the same chat UI works against both:
 *
 *  - `chatAdmin-sendMessage` — the super-admin-only assistant behind the floating
 *    chat bubble. No `venueID` / `app` in the payload. Authorization is the
 *    super-admin allowlist at the endpoint, conversations live under
 *    platform/chatAdmin/conversations, and the model always answers from the
 *    'super-admin' help-site context (docs/help-site/bot-training.json,
 *    published via the Chat Context tool). No admin actions are registered yet,
 *    so `confirmedAction` is rejected with invalid-argument until the backend
 *    registers one.
 *
 *  - `chat-sendMessage` — the venue-facing assistant embedded in the portal and
 *    POS apps. Requires `venueID` and the calling `app` (selects the help-site
 *    context). Super admins pass the `venueOwnerOrAdmin` gate, which is what
 *    lets the Regression tool drive it on a venue's behalf. Conversations live
 *    under venues/{venueID}/conversations.
 */

export type ChatSurface = 'admin' | 'venue';

/** Client apps the venue assistant can be called from (the `app` input). */
export type VenueChatApp = 'portal' | 'pos';

export interface ChatDestination {
  label: string;
  path: string;
}

export interface ChatTicketProposal {
  title: string;
  description: string;
  category: string;
}

/** Returned on `ticket_created` instead of the proposal. */
export interface ChatTicketReference {
  ticketId: string;
  label: string;
}

export function isTicketProposal(
  ticket: ChatTicketProposal | ChatTicketReference | null | undefined
): ticket is ChatTicketProposal {
  return !!ticket && typeof (ticket as ChatTicketProposal).title === 'string';
}

export interface ChatConfirmedAction {
  type: 'create_ticket';
  payload: ChatTicketProposal;
}

export type ChatMode = 'navigate' | 'clarify' | 'fallback' | 'confirm_ticket' | 'ticket_created';

export interface ChatDecision {
  mode: ChatMode;
  reply: string;
  intent: string;
  confidence: number;
  destination?: ChatDestination;
  ticket?: ChatTicketProposal | ChatTicketReference;
}

export interface SendMessageResponse {
  conversationId: string;
  decision: ChatDecision;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  mode?: ChatMode;
  intent?: string;
  confidence?: number;
  destination?: ChatDestination | null;
  ticket?: ChatTicketProposal | null;
}

/**
 * Per-turn diagnostics stored on assistant messages (stripped from the
 * sendMessage response, readable back through getConversation). Absent on
 * messages written before the shared chat core; null when unknown.
 */
export interface ChatTurnDiagnostics {
  model?: string | null;
  durationMs?: number | null;
  contextSha?: string | null;
  toolCalls?: Array<{ name: string; input: unknown }>;
  finishReason?: string | null;
  rawOutput?: string | null;
  error?: string | null;
  usage?: {
    inputTokens: number;
    outputTokens: number;
    cachedTokens: number;
    totalTokens: number;
  } | null;
}

export interface ChatTranscriptMessage extends ChatTurnDiagnostics {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: unknown;
  uid?: string;
  mode?: string;
  intent?: string;
  confidence?: number;
  destination?: ChatDestination | null;
  ticket?: Record<string, unknown> | null;
}

export interface ChatTranscript {
  id: string;
  uid: string;
  app?: string;
  status: 'active' | 'closed';
  messageCount: number;
  lastIntent: string | null;
  messages: ChatTranscriptMessage[];
  errorCount?: number;
  lastError?: string | null;
}

interface AdminSendMessagePayload {
  message?: string;
  conversationId?: string;
  confirmedAction?: ChatConfirmedAction;
}

interface VenueSendMessagePayload extends AdminSendMessagePayload {
  venueID: string;
  app: VenueChatApp;
}

// processTransaction wraps every callable's return value in `{ data }`.
type Envelope<T> = { data: T };

@Injectable({ providedIn: 'root' })
export class ChatService {
  private functions = inject(Functions);

  /**
   * Send a message to the super-admin assistant, creating or continuing a
   * platform-scoped conversation. Pass `confirmedAction` (message optional)
   * to finalize an action the assistant proposed.
   */
  async sendMessage(
    message: string,
    conversationId?: string,
    confirmedAction?: ChatConfirmedAction
  ): Promise<SendMessageResponse> {
    const payload: AdminSendMessagePayload = {};
    if (message) payload.message = message;
    if (conversationId) payload.conversationId = conversationId;
    if (confirmedAction) payload.confirmedAction = confirmedAction;

    const callable = httpsCallable<AdminSendMessagePayload, Envelope<SendMessageResponse>>(
      this.functions,
      'chatAdmin-sendMessage'
    );
    const result = await callable(payload);
    return result.data.data;
  }

  /**
   * Send a message to a venue's assistant as that venue's owner would from the
   * given client app. Super admins pass the endpoint's venueOwnerOrAdmin gate.
   * The conversation is scoped to `app`; continuing an id created under a
   * different app starts a new conversation server-side.
   */
  async sendVenueMessage(
    venueID: string,
    app: VenueChatApp,
    message: string,
    conversationId?: string,
    confirmedAction?: ChatConfirmedAction
  ): Promise<SendMessageResponse> {
    const payload: VenueSendMessagePayload = { venueID, app };
    if (message) payload.message = message;
    if (conversationId) payload.conversationId = conversationId;
    if (confirmedAction) payload.confirmedAction = confirmedAction;

    const callable = httpsCallable<VenueSendMessagePayload, Envelope<SendMessageResponse>>(
      this.functions,
      'chat-sendMessage'
    );
    const result = await callable(payload);
    return result.data.data;
  }

  /** Full transcript (with per-turn diagnostics) of a super-admin conversation. */
  async getAdminConversation(conversationId: string): Promise<ChatTranscript> {
    const callable = httpsCallable<{ conversationId: string }, Envelope<ChatTranscript>>(
      this.functions,
      'chatAdmin-getConversation'
    );
    const result = await callable({ conversationId });
    return result.data.data;
  }

  /** Full transcript (with per-turn diagnostics) of a venue conversation. */
  async getVenueConversation(venueID: string, conversationId: string): Promise<ChatTranscript> {
    const callable = httpsCallable<{ venueID: string; conversationId: string }, Envelope<ChatTranscript>>(
      this.functions,
      'chat-getConversation'
    );
    const result = await callable({ venueID, conversationId });
    return result.data.data;
  }
}
